/**
 * useSpatialFocus.js
 *
 * The ONE public client seam for spatial focus (`.codex/features/spatial-focus/README.md` -
 * "Client spatial-focus composable"). Grid, chart, task, CML and anomaly callers provide INTENT
 * only: an owner, a scope, an encoding/presentation and a viewer. They never provide dbIds, gray contexts,
 * target precedence, pulse sets or legend counts.
 *
 *   show({ owner, request|scope, encoding, presentation, viewer, useMasterTagAsPoiFallback? })
 *                                               resolve -> arbitrate -> render -> publish
 *   clear({ owner, viewer, reason })           drop the owned scene
 *   getActiveScene({ viewer })                  read its current owner without mutating it
 *   beginAnnotation({ viewer, subjectKey })    hand the active scene to the annotation flow
 *
 * It owns request cancellation, generation, loaded-model mapping, arbitration, Forge mutation and
 * legend publication - and nothing else. The three responsibilities are already separated:
 * `dashboard-widgets.service.js` owns the wire, `spatialFocusArbitrator.js` owns the decision, and
 * `forgeSpatialFocusAdapter.js` owns Forge. This module is only their order and their concurrency.
 *
 * CONCURRENCY (invariant 9). One module-scoped `WeakMap<viewer, state>` is shared by every
 * `useSpatialFocus()` call, so two components driving the same viewer share one generation rather
 * than fighting through two private authorities - and two viewers stay fully independent. Every
 * `show` aborts its viewer's in-flight request and bumps that viewer's generation; a result whose
 * generation has moved on is dropped before it can paint or publish. A -> B -> clear is therefore
 * deterministic no matter what order the responses arrive in.
 *
 * FAIL CLOSED (invariant 11). An empty scope, a typed refusal and an `unavailable` receipt all
 * clear the previous scene and say so. None of them widens to a whole-asset or unfiltered scope.
 * The one exception is an opted-in `swapInPlace` show (anomaly isolate, CML focus, task colouring):
 * there the previous scene belongs to another subject, not to a stale version of this request, so
 * it is kept and the caller says the new one could not be shown.
 *
 * Gate: `.codex/tests/spatial-focus-lifecycle.assert.cjs`
 */

import store from '@/store';
import { warn } from '@/utils/debugLogger';
import { spatialTargetLabel, relatedEquipmentResult } from '@/features/Dashboard/utils/dashboardReader';
import { clearThemingColors } from '@/utils/forgeUtils';
import dashboardWidgetsService from '@/features/Dashboard/service/dashboard-widgets.service';
import { buildSpatialRenderPlan, MIXED_COLOR } from '@/features/Dashboard/spatialFocus/spatialFocusArbitrator';
import {
  applySpatialRenderPlan,
  clearSpatialRender,
  listLoadedModels,
} from '@/features/Dashboard/spatialFocus/forgeSpatialFocusAdapter';

/** One shared per-viewer authority. See the CONCURRENCY note above. */
const statesByViewer = new WeakMap();

const STATE_CONTEXT = 'context';
const STATE_MIXED = 'mixed';

/** The legend row a MIXED conflict owns. Not an encoding value, so it needs a key of its own. */
const MIXED_ENTRY_KEY = '__mixed__';

/** A clear the viewer itself demands. It is not a widget opinion, so ownership does not gate it. */
const REASON_VIEWER_CLOSED = 'viewerClosed';

const UNKNOWN_ERROR_CODE = 'spatialFocusUnavailable';

function ensureState(viewer) {
  let state = statesByViewer.get(viewer);
  if (!state) {
    state = {
      generation: 0,
      controller: null,
      owner: null,
      encoding: null,
      result: null,
      plan: null,
      receipt: null,
      active: false,
      legendPublished: false,
    };
    statesByViewer.set(viewer, state);
  }
  return state;
}

/** An abort is not a failure: the caller that superseded this one owns the scene now. */
function isAbort(error, signal) {
  if (signal && signal.aborted) return true;
  if (!error) return false;
  return error.code === 'ERR_CANCELED'
    || error.name === 'CanceledError'
    || error.name === 'AbortError'
    || error.__CANCEL__ === true;
}

/**
 * The server's typed refusal, verbatim. `SpatialFocusErrorResponse` is `{ error, message }`, and
 * the transport status is part of the reason rather than the identity: `staleSourceRevision` is a
 * 409 the caller can retry after refreshing, every other refusal is a 400. So the CODE is what a
 * caller keys off, never the status.
 */
function toTypedError(error) {
  const body = error && error.response ? error.response.data : null;
  return {
    code: (body && body.error) || UNKNOWN_ERROR_CODE,
    message: (body && body.message) || (error && error.message) || 'Spatial focus is unavailable.',
    status: error && error.response ? error.response.status : null,
  };
}

function abortInFlight(state) {
  if (!state.controller) return;
  try {
    state.controller.abort();
  } catch (err) {
    warn('[SpatialFocus] abort failed', err);
  }
  state.controller = null;
}

/** Drop the owned scene and the legend it published. Never touches a scene we do not own. */
function discardScene(viewer, state, { keepIsolation = false } = {}) {
  const cleared = clearSpatialRender(viewer, { keepIsolation });
  if (state.legendPublished) {
    store.commit('SET_COLORING_LEGEND', null);
    state.legendPublished = false;
  }
  state.result = null;
  state.plan = null;
  state.receipt = null;
  state.owner = null;
  state.encoding = null;
  state.active = false;
  return cleared;
}

/** Clear pre-composable dashboard paint that has no adapter ledger to restore it. */
function clearLegacyScene(viewer, { keepIsolation = false } = {}) {
  const themed = store.getters?.GET_THEMED_DB_IDS;
  if (Array.isArray(themed) && themed.length) {
    try {
      clearThemingColors(viewer);
    } catch (err) {
      warn('[SpatialFocus] legacy theming reset failed', err);
    }
    store.commit('SET_THEMED_DB_IDS', []);
  }

  const forgeService = store.state?.forgeService;
  const extension = forgeService?.getMainExtension?.();
  const markerOwner = store.state?.TAGS?.cmlMarkerOwner;
  const markersOccupied = Array.isArray(forgeService?._cmlMarkers)
    && forgeService._cmlMarkers.length > 0;
  // mainExtension's empty isolate also clears the shared marker channel. De-isolate only when that
  // channel is empty or belongs to Spatial Focus; a foreign/legacy owner keeps its safety boundary.
  // `keepIsolation`: a swap isolates the new set in this same synchronous step, which replaces this one.
  const canEndIsolation = !keepIsolation && (!markersOccupied || markerOwner === 'spatialFocus');
  if (canEndIsolation && Array.isArray(extension?.isolatedDbids) && extension.isolatedDbids.length) {
    try {
      extension.isolateDbIds([], viewer.model);
    } catch (err) {
      warn('[SpatialFocus] legacy isolation reset failed', err);
    }
  }

  if (markerOwner === 'spatialFocus' && typeof forgeService?.clearCmlPointMarkers === 'function') {
    forgeService.clearCmlPointMarkers();
  }
  if (store.state?.coloringLegend) store.commit('SET_COLORING_LEGEND', null);
}

/**
 * Both resets end the previous isolation through `mainExtension.endIsolation()`, a whole-scene reset
 * that also switches ghosting off. `keepIsolation` skips ending it - only for a swap whose new
 * isolate runs in this same synchronous step and replaces it (see `swapInPlace` on `show`).
 */
function resetSceneForIntent(viewer, state, { keepIsolation = false } = {}) {
  const cleared = discardScene(viewer, state, { keepIsolation });
  clearLegacyScene(viewer, { keepIsolation });
  return cleared;
}

/**
 * Swaps whose answer is still on the wire, keyed by their own request controller so a superseded
 * swap settling late can never clear the flag a newer one set. While one is pending the previous
 * scene is still on screen, so the 3D page shows its (non-blocking) progress bar.
 */
const pendingSwaps = new Set();

function setSwapPending(controller, pending) {
  if (pending) pendingSwaps.add(controller);
  else pendingSwaps.delete(controller);
  store.commit('SET_SPATIAL_FOCUS_SWAP_PENDING', pendingSwaps.size > 0);
}

function normalizeHex(color) {
  return typeof color === 'string' && color.trim() ? color.trim().toLowerCase() : null;
}

/**
 * A KPI owns one aggregate status, not a per-row property. Validate its presentation atomically so
 * an incomplete label/color pair falls back to the existing property encoding or plain isolation.
 */
function normalizeConstantPresentation(presentation) {
  if (!presentation || presentation.kind !== 'constant') return null;
  const color = normalizeHex(presentation.color);
  const label = typeof presentation.label === 'string' ? presentation.label.trim() : '';
  if (!color || !label) return null;
  const displayName = typeof presentation.displayName === 'string'
    ? presentation.displayName.trim()
    : '';
  return {
    kind: 'constant',
    color,
    label,
    displayName: displayName || 'KPI status',
  };
}

function valueLabel(value) {
  if (value === null || value === undefined || value === '') return '(none)';
  return String(value);
}

/** Merge two diagnostic lists by code so the caller reads one aggregated set. */
function mergeDiagnostics(...lists) {
  const totals = new Map();
  lists.forEach((list) => (list || []).forEach((entry) => {
    if (!entry || !entry.code) return;
    totals.set(entry.code, (totals.get(entry.code) || 0) + (entry.count || 0));
  }));
  return [...totals.entries()]
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
}

function diagnosticCount(diagnostics, code) {
  const entry = (diagnostics || []).find((item) => item.code === code);
  return entry ? entry.count : 0;
}

/**
 * Geometry actually painted, grouped by the style that painted it. MIXED is its own bucket: it is
 * a conflict between styles, not one of them.
 */
function geometryByStyle(plan) {
  const totals = new Map();

  const add = (key, subjectKeys) => {
    if (!totals.has(key)) totals.set(key, { geometryObjects: 0, subjects: new Set() });
    const bucket = totals.get(key);
    bucket.geometryObjects += 1;
    (subjectKeys || []).forEach((subjectKey) => bucket.subjects.add(subjectKey));
  };

  (plan.elementClaims || []).forEach((claim) => {
    if (claim.state === STATE_CONTEXT) return;
    add(claim.state === STATE_MIXED ? MIXED_ENTRY_KEY : normalizeHex(claim.color), claim.subjectKeys);
  });
  (plan.markers || []).forEach((marker) => {
    add(marker.state === STATE_MIXED ? MIXED_ENTRY_KEY : normalizeHex(marker.color), marker.subjectKeys);
  });

  return totals;
}

/**
 * One legend row per encoding VALUE, plus one for MIXED.
 *
 * Records come from the occurrences (exact, one row is one record). Geometry counts are attributed
 * by colour, which is only sound while the value -> colour map is injective; when two values share
 * one colour the per-row geometry is published as null rather than double-counted. The scene-wide
 * totals in `counts` are always exact - they come straight from the render plan.
 */
function buildLegendEntries(plan, result, encoding, presentation) {
  if (presentation) {
    const geometry = geometryByStyle(plan).get(presentation.color);
    return [{
      key: '__constant__',
      value: presentation.label,
      label: presentation.label,
      color: presentation.color,
      state: 'target',
      count: plan.counts.records,
      records: plan.counts.records,
      targets: geometry ? geometry.subjects.size : 0,
      geometryObjects: geometry ? geometry.geometryObjects : 0,
    }];
  }

  const colorFor = encoding && typeof encoding.colorFor === 'function' ? encoding.colorFor : () => null;
  const byValue = new Map();

  (result.occurrences || []).forEach((occurrence) => {
    if (!occurrence) return;
    const key = valueLabel(occurrence.encodingValue);
    if (!byValue.has(key)) {
      byValue.set(key, { key, value: key, color: normalizeHex(colorFor(occurrence.encodingValue)), records: 0 });
    }
    byValue.get(key).records += 1;
  });

  const valuesPerColor = new Map();
  byValue.forEach((entry) => {
    if (!entry.color) return;
    valuesPerColor.set(entry.color, (valuesPerColor.get(entry.color) || 0) + 1);
  });

  const geometry = geometryByStyle(plan);

  const entries = [...byValue.values()].map((entry) => {
    const attributable = !!entry.color && valuesPerColor.get(entry.color) === 1;
    const bucket = attributable ? geometry.get(entry.color) : null;
    return {
      key: entry.key,
      // `value` and `count` are what the shipped ColoringLegend.vue keys, labels and counts by.
      // Task 4 converges the two shapes; until then the new fields are strictly additive.
      value: entry.value,
      label: entry.value,
      color: entry.color,
      state: 'target',
      count: entry.records,
      records: entry.records,
      targets: bucket ? bucket.subjects.size : null,
      geometryObjects: bucket ? bucket.geometryObjects : null,
    };
  });

  const mixed = geometry.get(MIXED_ENTRY_KEY);
  if (mixed) {
    entries.push({
      key: MIXED_ENTRY_KEY,
      value: 'Mixed',
      label: 'Mixed',
      color: MIXED_COLOR,
      state: STATE_MIXED,
      count: mixed.geometryObjects,
      records: null,
      targets: mixed.subjects.size,
      geometryObjects: mixed.geometryObjects,
    });
  }

  return entries;
}

/**
 * The row window the server actually applied, or null when there was none. A window only exists
 * when it EXCLUDED something: `recordsInWindow === totalFilteredBeforeWindow` is an all-matching
 * scope wearing a topBottom hat, and announcing it would be noise.
 */
function rowWindow(serverCounts) {
  const inWindow = Number(serverCounts.recordsInWindow);
  const beforeWindow = Number(serverCounts.totalFilteredBeforeWindow);
  if (!Number.isFinite(inWindow) || !Number.isFinite(beforeWindow)) return null;
  if (inWindow >= beforeWindow) return null;
  return { recordsInWindow: inWindow, totalFilteredBeforeWindow: beforeWindow };
}

/**
 * The legend receipt, built from what RENDERED rather than from what was asked for.
 *
 * The widget/chart title is primary and the colouring property secondary (the plan's legend-owner
 * constraint), so the owner title is published as `column` - the field the shipped
 * `ColoringLegend.vue` renders as its heading. Every other new field is additive.
 */
function buildLegend({ owner, encoding, presentation, plan, receipt, result, diagnostics }) {
  const hasColors = !!presentation || typeof encoding?.colorFor === 'function';
  const entries = hasColors ? buildLegendEntries(plan, result, encoding, presentation) : [];

  const property = hasColors && (presentation?.displayName
    || (encoding && (encoding.displayName || encoding.column))
    || null);
  const serverCounts = result.counts || {};

  return {
    owner: owner || null,
    targetLabel: spatialTargetLabel(result),
    // Interim reads (ColoringLegend.vue:52,41,45,49,58,64): column, entries, sources, selectedKey,
    // facets, refinable. Kept present and truthful so the live legend never renders blank.
    column: (owner && owner.title) || property,
    sources: [],
    selectedKey: null,
    facets: [],
    refinable: false,
    entries,
    // Additive receipt fields Task 4's ColoringLegend rewrite consumes.
    subtitle: property ? `Colored by ${property}` : null,
    property,
    counts: {
      records: plan.counts.records,
      targets: plan.counts.targets,
      // The SERVER's per-address total. Deliberately not added to the plan's per-object
      // `geometryObjects`: one address can carry many dbIds, so the two never sum.
      addresses: serverCounts.addresses === undefined ? null : serverCounts.addresses,
      unresolved: plan.counts.unresolved,
    },
    geometryObjects: plan.counts.geometryObjects,
    // The SERVER's row window, forwarded verbatim so the legend can state "10 of 13 filtered"
    // instead of presenting a ranked slice as the whole match. Only the resolver knows the
    // pre-window total (`/tables/data` counts OVER its own window), so it cannot be re-derived by a
    // caller, and a window a caller had to infer would be exactly the scope lie this feature
    // removes. Null when the scope was all-matching or the server reported neither number.
    window: rowWindow(serverCounts),
    partial: plan.partial || receipt.partial,
    conflicts: plan.counts.mixed,
    modelMismatches: diagnosticCount(diagnostics, 'modelMismatch'),
    truncated: result.truncated === true,
    diagnostics,
  };
}

/** The model-scoped element buckets one stable subject owns in the rendered scene. */
function subjectElements(plan, subjectKey) {
  const buckets = new Map();
  (plan.elementClaims || []).forEach((claim) => {
    if (claim.state === STATE_CONTEXT) return;
    if (!(claim.subjectKeys || []).includes(subjectKey)) return;
    if (!buckets.has(claim.modelUrn)) buckets.set(claim.modelUrn, { modelUrn: claim.modelUrn, dbIds: [] });
    buckets.get(claim.modelUrn).dbIds.push(claim.dbId);
  });
  return [...buckets.values()];
}

function subjectPoints(plan, subjectKey) {
  return (plan.markers || [])
    .filter((marker) => (marker.subjectKeys || []).includes(subjectKey))
    .map((marker) => marker.point);
}

/** Every stable subject that actually reached the screen as a TARGET. */
function renderedTargetSubjects(plan) {
  const keys = new Set();
  (plan.elementClaims || []).forEach((claim) => {
    if (claim.state === STATE_CONTEXT) return;
    (claim.subjectKeys || []).forEach((subjectKey) => keys.add(subjectKey));
  });
  (plan.markers || []).forEach((marker) => {
    (marker.subjectKeys || []).forEach((subjectKey) => keys.add(subjectKey));
  });
  return [...keys].sort();
}

/** Keep identity, kind and display metadata together so marker routing cannot drop the kind. */
function subjectsByKey(result) {
  const subjects = new Map();
  (result.subjects || []).forEach((subject) => {
    if (!subject?.subjectKey || subject.id === null || subject.id === undefined
      || subject.id === '' || !subject.kind) return;
    subjects.set(subject.subjectKey, {
      ...subject,
      id: String(subject.id),
      kind: String(subject.kind),
      displayLabel: subject.displayLabel == null ? null : String(subject.displayLabel).trim() || null,
    });
  });
  return subjects;
}

function subjectRecord(result, subjectKey) {
  const found = (result.subjects || []).find((subject) => subject && subject.subjectKey === subjectKey);
  return found || { subjectKey, kind: null, id: null, state: null };
}

/**
 * The one public seam. Every call shares the same per-viewer state, so this returns a thin façade
 * rather than a per-component authority.
 */
export function useSpatialFocus() {
  /**
   * Resolve, arbitrate, render and publish one spatial focus.
   *
   * @param {object}   input
   * @param {{widgetId: *, title: string}} input.owner the widget/chart that owns the scene
   * @param {{scope: object, focus?: object}} [input.request] typed query/row resolver envelope
   * @param {object}   input.scope    exactly one scope arm - query | rows | entities
   * @param {{column?: string, displayName?: string, colorFor?: Function}} [input.encoding]
   * @param {{kind:'constant',color:string,label:string,displayName?:string}} [input.presentation]
   * @param {object}   input.viewer   the Forge viewer to paint
   * @param {{setIntervalFn?: Function, clearIntervalFn?: Function}} [input.pulse] pulse timing seam
   * @param {Function} [input.shouldExecute] optional caller-owned freshness predicate
   * @param {boolean} [input.useMasterTagAsPoiFallback=true] whether a CML with no linked element may
   *        use its Master Tag's live elements as its target geometry
   * @param {boolean} [input.swapInPlace=false] opt-in for a user moving from one subject to the
   *        next (anomaly isolate, CML focus, task colouring). Nothing is cleared at the click: the
   *        previous scene stays on screen - with the 3D page's non-blocking progress bar - until the
   *        answer arrives. Then:
   *          - elements: ONE synchronous step replaces the isolation instead of ending it, so the
   *            whole model never flashes and the user's ghosting is kept, like a plain tag-card
   *            isolate;
   *          - markers only: there is nothing to ghost around, so the previous isolation (and with
   *            it ghosting) ends and the camera frames the point;
   *          - nothing to show (empty, unrenderable, refused): the requested action cannot happen,
   *            so the previous scene is kept exactly as it was; the status tells the caller to say so.
   * @returns {Promise<{status: 'rendered'|'empty'|'unrenderable'|'failed'|'aborted'|'superseded'
   *                            |'unavailable'}>}
   */
  async function show({
    owner,
    request,
    scope,
    encoding,
    presentation,
    viewer,
    pulse,
    useMasterTagAsPoiFallback,
    resolvedResult,
    displayMode = 'source',
    shouldExecute,
    swapInPlace = false,
  } = {}) {
    if (!viewer) return { status: 'unavailable' };
    const isRequestCurrent = () => !shouldExecute || shouldExecute();
    const swap = swapInPlace === true;
    if (!isRequestCurrent()) return { status: 'superseded' };

    const state = ensureState(viewer);

    // A missing scope is an empty focus INTENT, not permission to leave the previous owner's scene
    // hanging around. It supersedes any in-flight request and clears exactly what this composable
    // owns, just like a zero-row server receipt does after resolution.
    const intentScope = request?.scope || scope;
    if (!intentScope) {
      abortInFlight(state);
      state.generation += 1;
      resetSceneForIntent(viewer, state);
      return { status: 'unavailable', generation: state.generation, owner: owner || null };
    }

    // A new intent supersedes the old one before anything else happens: the previous request is
    // cancelled on the wire and its generation can no longer win.
    abortInFlight(state);
    state.generation += 1;
    const generation = state.generation;
    // The user's new intent owns an empty canvas immediately. Keeping the previous result painted
    // while this request is in flight makes stale isolation look like the answer to the new click.
    // A swap opts out: it keeps the previous scene until the answer (see `swapInPlace`).
    if (!swap) resetSceneForIntent(viewer, state);
    // An answer with nothing to show. The default path fails closed - its canvas is already clear,
    // this drops the bookkeeping. A swap keeps the previous scene: the user's action cannot happen.
    const nothingToShow = () => {
      if (!swap) discardScene(viewer, state);
    };

    const controller = new AbortController();
    state.controller = controller;
    const externallySuperseded = () => {
      if (state.controller === controller) state.controller = null;
      return { status: 'superseded', generation };
    };

    // `state.owner` is claimed at RENDER time, not here. It means "who owns what is on screen", so
    // a widget whose request is still in flight must not be able to refuse the currently-painting
    // widget's own clear.
    let result = null;
    if (swap) setSwapPending(controller, true);
    try {
      // Omit the compatibility default so a rolling UI deployment can still talk to the older,
      // strict request contract. Only the user's explicit OFF choice needs the new API field.
      const resolveRequest = request || { scope: intentScope };
      if (!request && useMasterTagAsPoiFallback === false) {
        resolveRequest.useMasterTagAsPoiFallback = false;
      }
      const response = resolvedResult ? { data: resolvedResult } : await dashboardWidgetsService.resolveSpatialFocus(
        resolveRequest,
        { signal: controller.signal },
      );
      result = response ? response.data : null;
    } catch (error) {
      // Order matters. An abort and a moved generation are BOTH true when a newer show supersedes
      // this one, and the abort is the proximate cause - the request never produced an answer at
      // all. Reporting it first keeps the two mechanisms distinguishable: `aborted` means the wire
      // was cancelled, `superseded` means an answer arrived too late to matter. Either way the
      // scene is left alone, because the newer show now owns it.
      if (isAbort(error, controller.signal)) return { status: 'aborted', generation };
      if (!isRequestCurrent()) return externallySuperseded();
      if (generation !== state.generation) return { status: 'superseded', generation };

      state.controller = null;
      const typed = toTypedError(error);
      // A refused focus paints nothing: the default path fails closed, a swap keeps the previous
      // scene (see `nothingToShow`).
      nothingToShow();
      return { status: 'failed', generation, owner: owner || null, error: typed };
    } finally {
      // Settled one way or another; the render below (if any) runs in this same synchronous turn.
      if (swap) setSwapPending(controller, false);
    }

    if (!isRequestCurrent()) return externallySuperseded();
    if (generation !== state.generation) return { status: 'superseded', generation };
    state.controller = null;

    if (!result || result.state === 'unavailable') {
      nothingToShow();
      return {
        status: 'failed',
        generation,
        owner: owner || null,
        error: {
          code: UNKNOWN_ERROR_CODE,
          message: 'This source cannot currently resolve an exact spatial focus.',
          status: null,
        },
      };
    }

    if (displayMode === 'relatedEquipment') result = relatedEquipmentResult(result);
    const constantPresentation = normalizeConstantPresentation(presentation);
    const renderEncoding = constantPresentation
      ? { colorFor: () => constantPresentation.color }
      : encoding;
    const plan = buildSpatialRenderPlan({
      occurrences: result.occurrences,
      subjects: result.subjects,
      addresses: result.addresses,
      encoding: renderEncoding,
      loadedModels: listLoadedModels(viewer),
    });

    // The models were read and the plan built while nothing was awaited, but a synchronous caller
    // could still have superseded this scene during the await above.
    if (generation !== state.generation) return { status: 'superseded', generation };

    // "Nothing on screen" has two very different causes and they must not share a status.
    //
    //   empty        the scope matched no rows at all;
    //   unrenderable the scope resolved rows and then lost EVERY one of them at the viewer - the
    //                strict visible-target gate, a model mismatch, an unplaceable point.
    //
    // The second still has a real row scope behind it, so `records`, `unresolved` and the
    // diagnostics all carry meaning a caller has to be able to show. Collapsing the two would force
    // every caller to re-derive the difference from the counts - exactly the re-derivation this
    // seam exists to delete. Neither publishes a colouring legend: nothing is coloured on screen,
    // and a legend for an invisible scene is the silent outcome invariant 11 forbids.
    if (!plan.elementClaims.length && !plan.markers.length) {
      nothingToShow();
      return {
        status: plan.counts.records > 0 ? 'unrenderable' : 'empty',
        generation,
        owner: owner || null,
        counts: plan.counts,
        diagnostics: plan.diagnostics,
        partial: plan.partial,
      };
    }

    if (!isRequestCurrent()) return externallySuperseded();
    // The swap itself: drop the old scene's paint, markers and pulse but NOT its isolation - the
    // render below isolates the new set in this same synchronous step, which replaces it. No frame
    // is drawn in between, so the user goes from the old focus straight to the new one.
    //
    // Only when this scene isolates something. A marker-only answer has no element to isolate or
    // ghost around, so the previous isolation ends here (ghosting with it, through endIsolation) and
    // the render frames the point - otherwise the last focus's geometry would stay under the marker.
    if (swap) resetSceneForIntent(viewer, state, { keepIsolation: plan.elementClaims.length > 0 });
    const receipt = applySpatialRenderPlan(viewer, plan, {
      pulse,
      subjectsByKey: subjectsByKey(result),
    });
    const diagnostics = mergeDiagnostics(plan.diagnostics, receipt.diagnostics);

    state.result = result;
    state.plan = plan;
    state.receipt = receipt;
    state.owner = owner || null;
    state.encoding = renderEncoding || null;
    state.active = true;

    const legend = buildLegend({
      owner,
      encoding,
      presentation: constantPresentation,
      plan,
      receipt,
      result,
      diagnostics,
    });
    store.commit('SET_COLORING_LEGEND', legend);
    state.legendPublished = legend !== null;

    return {
      status: 'rendered',
      generation,
      owner: owner || null,
      counts: plan.counts,
      receipt,
      diagnostics,
      legend,
    };
  }

  /**
   * Return the minimal ownership snapshot for the scene currently published in one viewer.
   * Marker layers are shared and may be replaced independently, so their owner token is not proof
   * that the element scene disappeared. This read performs no Forge, legend or generation work.
   */
  function getActiveScene({ viewer } = {}) {
    const state = viewer ? statesByViewer.get(viewer) : null;
    if (!state || !state.active) return null;
    return {
      owner: state.owner ? { ...state.owner } : null,
      generation: state.generation,
    };
  }

  /**
   * Drop the owned scene.
   *
   * Ownership is honoured so a sibling widget cannot blank the scene another widget published; a
   * `viewerClosed` reason (or a caller that names no owner) overrides it, because the viewer going
   * away is not a widget opinion.
   *
   * @returns {boolean} whether there was an owned scene to clear - a second clear is a true no-op
   */
  function clear({ owner, viewer, reason } = {}) {
    const state = viewer ? statesByViewer.get(viewer) : null;
    if (!state) return false;

    const foreignOwner = owner && owner.widgetId !== undefined
      && state.owner && state.owner.widgetId !== undefined
      && owner.widgetId !== state.owner.widgetId;
    if (foreignOwner && reason !== REASON_VIEWER_CLOSED) return false;

    const hadWork = state.active || !!state.controller;

    abortInFlight(state);
    // A clear is its own generation: a result still in flight cannot repaint what was cleared.
    state.generation += 1;

    const cleared = resetSceneForIntent(viewer, state);

    return hadWork || cleared;
  }

  /**
   * Hand the active scene to the annotation flow.
   *
   * It reads state and returns it. It performs NO navigation and NO persistence - Plan D consumes
   * this snapshot. Several stable subjects require an explicit visible choice; no first candidate
   * is ever selected on the user's behalf.
   *
   * @param {{viewer: object, subjectKey?: string}} input
   */
  function beginAnnotation({ viewer, subjectKey } = {}) {
    const state = viewer ? statesByViewer.get(viewer) : null;
    if (!state || !state.active || !state.plan) return { status: 'unavailable' };

    const { plan, result, receipt } = state;
    const candidateKeys = renderedTargetSubjects(plan);
    if (!candidateKeys.length) return { status: 'unavailable' };

    const candidates = candidateKeys.map((key) => ({
      subject: subjectRecord(result, key),
      elements: subjectElements(plan, key),
      points: subjectPoints(plan, key),
    }));

    const chosen = subjectKey
      ? candidates.find((candidate) => candidate.subject.subjectKey === subjectKey)
      : (candidates.length === 1 ? candidates[0] : null);

    if (!chosen) {
      if (subjectKey) return { status: 'unavailable' };
      return { status: 'choiceRequired', owner: state.owner, candidates, receipt };
    }

    return {
      status: 'ready',
      owner: state.owner,
      subject: chosen.subject,
      // A multi-solid target passes EVERY address belonging to its stable subject, model-scoped.
      elements: chosen.elements,
      points: chosen.points,
      receipt,
      // The first delivery cannot durably say which CML/anomaly an annotation is about; saying so
      // is the point. Opaque JSON persistence is forbidden and a typed schema needs a migration.
      subjectPersistence: 'unsupported',
    };
  }

  return { show, clear, getActiveScene, beginAnnotation };
}

export default useSpatialFocus;
